import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft,
  Search,
  UserPlus,
  Shield,
  User,
  Loader2,
  MoreVertical,
  Trash2,
  Ticket,
  Copy,
  Plus,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  MessageCircle,
  MessageSquare,
  Edit,
  Megaphone,
  Bell,
  Sparkles,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { format } from "date-fns";
import { Switch } from "@/components/ui/switch";
import { ServiceManagement } from "@/components/admin/ServiceManagement";

interface Agent {
  user_id: string;
  role: "agent" | "admin";
  created_at: string;
  profile?: {
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  };
}

interface InviteCode {
  id: string;
  code: string;
  status: string;
  expires_at: string | null;
  created_at: string;
  used_at: string | null;
  issued_to_email: string | null;
  notes: string | null;
}

interface QuickReply {
  id: string;
  title: string;
  content: string;
  category: string | null;
  is_global: boolean;
  service_id: string | null;
  created_at: string;
}

interface Service {
  id: string;
  name: string;
}

interface PromotionalBanner {
  id: string;
  title: string;
  content: string;
  is_active: boolean;
  expires_at: string | null;
  created_at: string;
}

export default function AdminPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [inviteCodes, setInviteCodes] = useState<InviteCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newAgentEmail, setNewAgentEmail] = useState("");
  const [newAgentRole, setNewAgentRole] = useState<"agent" | "admin">("agent");
  const [adding, setAdding] = useState(false);
  
  // Invite codes state
  const [codeDialogOpen, setCodeDialogOpen] = useState(false);
  const [newCodeEmail, setNewCodeEmail] = useState("");
  const [newCodeNotes, setNewCodeNotes] = useState("");
  const [creatingCode, setCreatingCode] = useState(false);

  // Quick replies state
  const [quickReplies, setQuickReplies] = useState<QuickReply[]>([]);
  const [quickReplyDialogOpen, setQuickReplyDialogOpen] = useState(false);
  const [editingQuickReply, setEditingQuickReply] = useState<QuickReply | null>(null);
  const [newQuickReplyTitle, setNewQuickReplyTitle] = useState("");
  const [newQuickReplyContent, setNewQuickReplyContent] = useState("");
  const [newQuickReplyCategory, setNewQuickReplyCategory] = useState("");
  const [newQuickReplyServiceId, setNewQuickReplyServiceId] = useState<string | null>(null);
  const [savingQuickReply, setSavingQuickReply] = useState(false);
  const [services, setServices] = useState<Service[]>([]);

  // Promotional banners state
  const [banners, setBanners] = useState<PromotionalBanner[]>([]);
  const [bannerDialogOpen, setBannerDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<PromotionalBanner | null>(null);
  const [newBannerTitle, setNewBannerTitle] = useState("");
  const [newBannerContent, setNewBannerContent] = useState("");
  const [newBannerSendNotification, setNewBannerSendNotification] = useState(true);
  const [savingBanner, setSavingBanner] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      navigate("/agent");
      return;
    }

    const fetchData = async () => {
      // Fetch agents
      const { data: agentData, error: agentError } = await supabase
        .from("user_roles")
        .select(`
          user_id,
          role,
          created_at
        `)
        .in("role", ["agent", "admin"])
        .order("created_at", { ascending: false });

      if (!agentError && agentData) {
        // Fetch profiles separately
        const userIds = agentData.map(a => a.user_id);
        const { data: profilesData } = await supabase
          .from("profiles")
          .select("id, first_name, last_name, avatar_url")
          .in("id", userIds);

        const profilesMap = new Map(profilesData?.map(p => [p.id, p]) || []);
        
        const formatted = agentData.map((item: any) => ({
          ...item,
          profile: profilesMap.get(item.user_id),
        }));
        setAgents(formatted);
      }

      // Fetch invite codes
      const { data: codesData, error: codesError } = await supabase
        .from("invite_codes")
        .select("*")
        .order("created_at", { ascending: false });

      if (!codesError && codesData) {
        setInviteCodes(codesData);
      }

      // Fetch quick replies
      const { data: quickRepliesData, error: quickRepliesError } = await supabase
        .from("quick_replies")
        .select("*")
        .order("created_at", { ascending: false });

      if (!quickRepliesError && quickRepliesData) {
        setQuickReplies(quickRepliesData);
      }

      // Fetch services for quick reply filtering
      const { data: servicesData } = await supabase
        .from("services")
        .select("id, name")
        .order("sort_order", { ascending: true });

      if (servicesData) {
        setServices(servicesData);
      }

      // Fetch promotional banners
      const { data: bannersData, error: bannersError } = await supabase
        .from("promotional_banners")
        .select("*")
        .order("created_at", { ascending: false });

      if (!bannersError && bannersData) {
        setBanners(bannersData as PromotionalBanner[]);
      }

      setLoading(false);
    };

    fetchData();
  }, [isAdmin, navigate]);

  const handleAddAgent = async () => {
    if (!newAgentEmail.trim()) {
      toast.error("Veuillez entrer une adresse email");
      return;
    }

    setAdding(true);
    toast.info(
      "Note: L'utilisateur doit avoir un compte existant. Contactez le support pour une configuration en masse."
    );

    setAdding(false);
    setAddDialogOpen(false);
  };

  const handleChangeRole = async (userId: string, newRole: "agent" | "admin") => {
    const { error } = await supabase
      .from("user_roles")
      .update({ role: newRole })
      .eq("user_id", userId)
      .in("role", ["agent", "admin"]);

    if (error) {
      toast.error("Échec de la mise à jour du rôle");
    } else {
      toast.success(`Rôle mis à jour vers ${newRole === 'admin' ? 'Administrateur' : 'Concierge'}`);
      setAgents((prev) =>
        prev.map((a) => (a.user_id === userId ? { ...a, role: newRole } : a))
      );
    }
  };

  const handleRemoveAgent = async (userId: string) => {
    const { error } = await supabase
      .from("user_roles")
      .delete()
      .eq("user_id", userId)
      .in("role", ["agent", "admin"]);

    if (error) {
      toast.error("Échec de la suppression");
    } else {
      toast.success("Membre retiré");
      setAgents((prev) => prev.filter((a) => a.user_id !== userId));
    }
  };

  // Generate random code
  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const handleCreateCode = async () => {
    setCreatingCode(true);

    const code = generateCode();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 30); // 30 days expiry

    const { data, error } = await supabase
      .from("invite_codes")
      .insert({
        code,
        status: "unused",
        expires_at: expiresAt.toISOString(),
        issued_to_email: newCodeEmail.trim() || null,
        notes: newCodeNotes.trim() || null,
      })
      .select()
      .single();

    if (error) {
      toast.error("Échec de la création du code");
    } else {
      toast.success("Code créé avec succès");
      setInviteCodes((prev) => [data, ...prev]);
      setCodeDialogOpen(false);
      setNewCodeEmail("");
      setNewCodeNotes("");
      
      // Copy to clipboard
      navigator.clipboard.writeText(code);
      toast.info("Code copié dans le presse-papiers");
    }

    setCreatingCode(false);
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success("Code copié");
  };

  const handleRevokeCode = async (id: string) => {
    const { error } = await supabase
      .from("invite_codes")
      .update({ status: "revoked" })
      .eq("id", id);

    if (error) {
      toast.error("Échec de la révocation");
    } else {
      toast.success("Code révoqué");
      setInviteCodes((prev) =>
        prev.map((c) => (c.id === id ? { ...c, status: "revoked" } : c))
      );
    }
  };

  // Quick Replies handlers
  const openQuickReplyDialog = (reply?: QuickReply) => {
    if (reply) {
      setEditingQuickReply(reply);
      setNewQuickReplyTitle(reply.title);
      setNewQuickReplyContent(reply.content);
      setNewQuickReplyCategory(reply.category || "");
      setNewQuickReplyServiceId(reply.service_id);
    } else {
      setEditingQuickReply(null);
      setNewQuickReplyTitle("");
      setNewQuickReplyContent("");
      setNewQuickReplyCategory("");
      setNewQuickReplyServiceId(null);
    }
    setQuickReplyDialogOpen(true);
  };

  const handleSaveQuickReply = async () => {
    if (!newQuickReplyTitle.trim() || !newQuickReplyContent.trim()) {
      toast.error("Veuillez remplir le titre et le contenu");
      return;
    }

    setSavingQuickReply(true);

    if (editingQuickReply) {
      // Update
      const { error } = await supabase
        .from("quick_replies")
        .update({
          title: newQuickReplyTitle.trim(),
          content: newQuickReplyContent.trim(),
          category: newQuickReplyCategory.trim() || null,
          service_id: newQuickReplyServiceId,
        })
        .eq("id", editingQuickReply.id);

      if (error) {
        toast.error("Échec de la mise à jour");
      } else {
        toast.success("Réponse rapide mise à jour");
        setQuickReplies((prev) =>
          prev.map((r) =>
            r.id === editingQuickReply.id
              ? {
                  ...r,
                  title: newQuickReplyTitle.trim(),
                  content: newQuickReplyContent.trim(),
                  category: newQuickReplyCategory.trim() || null,
                  service_id: newQuickReplyServiceId,
                }
              : r
          )
        );
        setQuickReplyDialogOpen(false);
      }
    } else {
      // Create
      const { data, error } = await supabase
        .from("quick_replies")
        .insert({
          title: newQuickReplyTitle.trim(),
          content: newQuickReplyContent.trim(),
          category: newQuickReplyCategory.trim() || null,
          service_id: newQuickReplyServiceId,
          is_global: newQuickReplyServiceId === null,
        })
        .select()
        .single();

      if (error) {
        toast.error("Échec de la création");
      } else {
        toast.success("Réponse rapide créée");
        setQuickReplies((prev) => [data, ...prev]);
        setQuickReplyDialogOpen(false);
      }
    }

    setSavingQuickReply(false);
  };

  const handleDeleteQuickReply = async (id: string) => {
    const { error } = await supabase.from("quick_replies").delete().eq("id", id);

    if (error) {
      toast.error("Échec de la suppression");
    } else {
      toast.success("Réponse rapide supprimée");
      setQuickReplies((prev) => prev.filter((r) => r.id !== id));
    }
  };

  // Banner handlers
  const openBannerDialog = (banner?: PromotionalBanner) => {
    if (banner) {
      setEditingBanner(banner);
      setNewBannerTitle(banner.title);
      setNewBannerContent(banner.content);
      setNewBannerSendNotification(false);
    } else {
      setEditingBanner(null);
      setNewBannerTitle("");
      setNewBannerContent("");
      setNewBannerSendNotification(true);
    }
    setBannerDialogOpen(true);
  };

  const sendBannerNotifications = async (title: string, content: string) => {
    try {
      // Get users who actually have push subscriptions (not just the flag)
      const { data: subscriptions } = await supabase
        .from("push_subscriptions")
        .select("user_id")
        .not("endpoint", "is", null);

      if (!subscriptions || subscriptions.length === 0) {
        console.log("[Banner] No users with active push subscriptions");
        toast.info("Aucun utilisateur avec notifications actives");
        return;
      }

      // Get unique user IDs
      const uniqueUserIds = [...new Set(subscriptions.map(s => s.user_id))];
      console.log(`[Banner] Sending notifications to ${uniqueUserIds.length} users`);

      // Send notifications in batches
      let successCount = 0;
      const batchSize = 10;
      for (let i = 0; i < uniqueUserIds.length; i += batchSize) {
        const batch = uniqueUserIds.slice(i, i + batchSize);
        const results = await Promise.allSettled(
          batch.map((userId) =>
            supabase.functions.invoke("send-push-notification", {
              body: {
                userId,
                title: `📢 ${title}`,
                body: content.substring(0, 100),
                data: { type: "promotion", url: "/services" },
              },
            })
          )
        );
        
        successCount += results.filter(r => r.status === "fulfilled" && !(r.value as any).error).length;
      }

      toast.success(`Notification envoyée à ${successCount}/${uniqueUserIds.length} utilisateurs`);
    } catch (error) {
      console.error("[Banner] Failed to send notifications:", error);
      toast.error("Erreur lors de l'envoi des notifications");
    }
  };

  const handleSaveBanner = async () => {
    if (!newBannerTitle.trim() || !newBannerContent.trim()) {
      toast.error("Veuillez remplir le titre et le contenu");
      return;
    }

    setSavingBanner(true);

    if (editingBanner) {
      // Update
      const { error } = await supabase
        .from("promotional_banners")
        .update({
          title: newBannerTitle.trim(),
          content: newBannerContent.trim(),
        })
        .eq("id", editingBanner.id);

      if (error) {
        toast.error("Échec de la mise à jour");
      } else {
        toast.success("Annonce mise à jour");
        setBanners((prev) =>
          prev.map((b) =>
            b.id === editingBanner.id
              ? { ...b, title: newBannerTitle.trim(), content: newBannerContent.trim() }
              : b
          )
        );
        setBannerDialogOpen(false);
      }
    } else {
      // Create
      const { data: userData } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from("promotional_banners")
        .insert({
          title: newBannerTitle.trim(),
          content: newBannerContent.trim(),
          created_by: userData.user?.id,
        })
        .select()
        .single();

      if (error) {
        toast.error("Échec de la création");
      } else {
        toast.success("Annonce créée");
        setBanners((prev) => [data as PromotionalBanner, ...prev]);
        setBannerDialogOpen(false);

        // Send push notifications if enabled
        if (newBannerSendNotification) {
          await sendBannerNotifications(newBannerTitle.trim(), newBannerContent.trim());
        }
      }
    }

    setSavingBanner(false);
  };

  const handleToggleBannerActive = async (banner: PromotionalBanner) => {
    const { error } = await supabase
      .from("promotional_banners")
      .update({ is_active: !banner.is_active })
      .eq("id", banner.id);

    if (error) {
      toast.error("Échec de la mise à jour");
    } else {
      setBanners((prev) =>
        prev.map((b) =>
          b.id === banner.id ? { ...b, is_active: !b.is_active } : b
        )
      );
    }
  };

  const handleDeleteBanner = async (id: string) => {
    const { error } = await supabase.from("promotional_banners").delete().eq("id", id);

    if (error) {
      toast.error("Échec de la suppression");
    } else {
      toast.success("Annonce supprimée");
      setBanners((prev) => prev.filter((b) => b.id !== id));
    }
  };

  const getCodeStatusBadge = (code: InviteCode) => {
    if (code.status === "used") {
      return (
        <Badge variant="outline" className="bg-success/10 text-success border-success/20">
          <CheckCircle size={12} className="mr-1" />
          Utilisé
        </Badge>
      );
    }
    if (code.status === "revoked") {
      return (
        <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/20">
          <XCircle size={12} className="mr-1" />
          Révoqué
        </Badge>
      );
    }
    if (code.expires_at && new Date(code.expires_at) < new Date()) {
      return (
        <Badge variant="outline" className="bg-muted text-muted-foreground">
          <Clock size={12} className="mr-1" />
          Expiré
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
        <Ticket size={12} className="mr-1" />
        Actif
      </Badge>
    );
  };

  const filteredAgents = agents.filter((agent) => {
    const fullName = `${agent.profile?.first_name || ""} ${
      agent.profile?.last_name || ""
    }`.toLowerCase();
    return fullName.includes(searchQuery.toLowerCase());
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center gap-3 sm:gap-4 max-w-4xl mx-auto">
          <Link to="/agent">
            <Button variant="ghost" size="icon" className="h-9 w-9">
              <ArrowLeft size={18} />
            </Button>
          </Link>
          <div>
            <h1 className="font-serif text-lg sm:text-xl font-semibold text-foreground">
              Administration
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Gérez l'équipe, les services et les paramètres
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        <Tabs defaultValue="team" className="space-y-4 sm:space-y-6">
          <TabsList className="grid w-full grid-cols-5 h-10 sm:h-auto">
            <TabsTrigger value="team" className="gap-1 sm:gap-2 text-[10px] sm:text-sm px-1 sm:px-3">
              <User size={14} className="sm:hidden" />
              <User size={16} className="hidden sm:block" />
              <span className="hidden sm:inline">Équipe</span>
            </TabsTrigger>
            <TabsTrigger value="services" className="gap-1 sm:gap-2 text-[10px] sm:text-sm px-1 sm:px-3">
              <Sparkles size={14} className="sm:hidden" />
              <Sparkles size={16} className="hidden sm:block" />
              <span className="hidden sm:inline">Services</span>
            </TabsTrigger>
            <TabsTrigger value="codes" className="gap-1 sm:gap-2 text-[10px] sm:text-sm px-1 sm:px-3">
              <Ticket size={14} className="sm:hidden" />
              <Ticket size={16} className="hidden sm:block" />
              <span className="hidden sm:inline">Codes</span>
            </TabsTrigger>
            <TabsTrigger value="replies" className="gap-1 sm:gap-2 text-[10px] sm:text-sm px-1 sm:px-3">
              <MessageSquare size={14} className="sm:hidden" />
              <MessageSquare size={16} className="hidden sm:block" />
              <span className="hidden sm:inline">Réponses</span>
            </TabsTrigger>
            <TabsTrigger value="banners" className="gap-1 sm:gap-2 text-[10px] sm:text-sm px-1 sm:px-3">
              <Megaphone size={14} className="sm:hidden" />
              <Megaphone size={16} className="hidden sm:block" />
              <span className="hidden sm:inline">Annonces</span>
            </TabsTrigger>
          </TabsList>

          {/* Services Tab */}
          <TabsContent value="services" className="space-y-4">
            <ServiceManagement />
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team" className="space-y-4">
            {/* Actions */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  placeholder="Rechercher un membre..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="gold">
                    <UserPlus size={18} />
                    <span>Ajouter</span>
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-serif">Ajouter un membre</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Adresse email</Label>
                      <Input
                        type="email"
                        placeholder="agent@example.com"
                        value={newAgentEmail}
                        onChange={(e) => setNewAgentEmail(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Rôle</Label>
                      <div className="flex gap-2">
                        <Button
                          variant={newAgentRole === "agent" ? "default" : "outline"}
                          onClick={() => setNewAgentRole("agent")}
                          className="flex-1"
                        >
                          <MessageCircle size={16} />
                          Concierge
                        </Button>
                        <Button
                          variant={newAgentRole === "admin" ? "default" : "outline"}
                          onClick={() => setNewAgentRole("admin")}
                          className="flex-1"
                        >
                          <Shield size={16} />
                          Admin
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {newAgentRole === "agent" 
                          ? "Le concierge peut uniquement répondre aux conversations."
                          : "L'admin peut gérer l'équipe, les codes et toutes les conversations."}
                      </p>
                    </div>
                    <Button
                      onClick={handleAddAgent}
                      disabled={adding}
                      className="w-full"
                      variant="gold"
                    >
                      {adding ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        "Ajouter le membre"
                      )}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Agents List */}
            <div className="bg-card rounded-xl border border-border overflow-hidden">
              <div className="px-4 py-3 border-b border-border">
                <h2 className="font-medium text-foreground">
                  Membres de l'équipe ({filteredAgents.length})
                </h2>
              </div>

              {filteredAgents.length === 0 ? (
                <div className="p-8 text-center">
                  <User size={40} className="mx-auto text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">
                    Aucun membre trouvé
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {filteredAgents.map((agent) => (
                    <div
                      key={agent.user_id}
                      className="flex items-center justify-between px-4 py-3 hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          {agent.role === "admin" ? (
                            <Shield size={18} className="text-primary" />
                          ) : (
                            <MessageCircle size={18} className="text-primary" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-foreground">
                            {agent.profile?.first_name || "Utilisateur"}{" "}
                            {agent.profile?.last_name || "inconnu"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Ajouté le {format(new Date(agent.created_at), "dd/MM/yyyy")}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <Badge
                          variant="outline"
                          className={
                            agent.role === "admin"
                              ? "border-primary/30 text-primary"
                              : ""
                          }
                        >
                          {agent.role === "admin" && <Shield size={12} className="mr-1" />}
                          {agent.role === "admin" ? "Admin" : "Concierge"}
                        </Badge>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical size={18} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() =>
                                handleChangeRole(
                                  agent.user_id,
                                  agent.role === "admin" ? "agent" : "admin"
                                )
                              }
                            >
                              {agent.role === "admin" ? (
                                <>
                                  <MessageCircle size={14} className="mr-2" />
                                  Passer en Concierge
                                </>
                              ) : (
                                <>
                                  <Shield size={14} className="mr-2" />
                                  Promouvoir Admin
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleRemoveAgent(agent.user_id)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 size={14} className="mr-2" />
                              Retirer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Invite Codes Tab */}
          <TabsContent value="codes" className="space-y-4">
            {/* Actions */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-medium text-foreground">Codes d'invitation</h2>
                <p className="text-sm text-muted-foreground">
                  Créez des codes uniques pour l'inscription des clients
                </p>
              </div>

              <Dialog open={codeDialogOpen} onOpenChange={setCodeDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="gold">
                    <Plus size={18} />
                    <span>Nouveau code</span>
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-serif">Créer un code d'invitation</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Email du destinataire (optionnel)</Label>
                      <Input
                        type="email"
                        placeholder="client@example.com"
                        value={newCodeEmail}
                        onChange={(e) => setNewCodeEmail(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        Pour référence uniquement, n'envoie pas d'email
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>Notes (optionnel)</Label>
                      <Input
                        placeholder="Ex: Client VIP, Référé par..."
                        value={newCodeNotes}
                        onChange={(e) => setNewCodeNotes(e.target.value)}
                      />
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Validité</p>
                      <p className="text-sm text-foreground flex items-center gap-2">
                        <Calendar size={14} />
                        30 jours à partir de la création
                      </p>
                    </div>
                    <Button
                      onClick={handleCreateCode}
                      disabled={creatingCode}
                      className="w-full"
                      variant="gold"
                    >
                      {creatingCode ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Ticket size={16} />
                          Générer le code
                        </>
                      )}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Codes List */}
            <div className="bg-card rounded-xl border border-border overflow-hidden">
              {inviteCodes.length === 0 ? (
                <div className="p-8 text-center">
                  <Ticket size={40} className="mx-auto text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">
                    Aucun code créé
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {inviteCodes.map((code) => (
                    <div
                      key={code.id}
                      className="flex items-center justify-between px-4 py-3 hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center font-mono font-bold text-primary">
                          {code.code.slice(0, 2)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <code className="font-mono text-sm font-medium text-foreground bg-muted px-2 py-0.5 rounded">
                              {code.code}
                            </code>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => handleCopyCode(code.code)}
                            >
                              <Copy size={12} />
                            </Button>
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                            <span>Créé le {format(new Date(code.created_at), "dd/MM/yyyy")}</span>
                            {code.issued_to_email && (
                              <>
                                <span>•</span>
                                <span>{code.issued_to_email}</span>
                              </>
                            )}
                            {code.notes && (
                              <>
                                <span>•</span>
                                <span>{code.notes}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        {getCodeStatusBadge(code)}

                        {code.status === "unused" && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical size={18} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleCopyCode(code.code)}>
                                <Copy size={14} className="mr-2" />
                                Copier
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleRevokeCode(code.id)}
                                className="text-destructive focus:text-destructive"
                              >
                                <XCircle size={14} className="mr-2" />
                                Révoquer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Quick Replies Tab */}
          <TabsContent value="replies" className="space-y-4">
            {/* Actions */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-medium text-foreground">Réponses rapides</h2>
                <p className="text-sm text-muted-foreground">
                  Réponses pré-définies pour les concierges
                </p>
              </div>

              <Dialog open={quickReplyDialogOpen} onOpenChange={setQuickReplyDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="gold" onClick={() => openQuickReplyDialog()}>
                    <Plus size={18} />
                    <span>Nouvelle réponse</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-serif">
                      {editingQuickReply ? "Modifier la réponse rapide" : "Créer une réponse rapide"}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Titre</Label>
                      <Input
                        placeholder="Ex: Bienvenue"
                        value={newQuickReplyTitle}
                        onChange={(e) => setNewQuickReplyTitle(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Catégorie (optionnel)</Label>
                      <Input
                        placeholder="Ex: Salutations, Informations..."
                        value={newQuickReplyCategory}
                        onChange={(e) => setNewQuickReplyCategory(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Service (optionnel)</Label>
                      <select
                        className="w-full h-10 px-3 py-2 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                        value={newQuickReplyServiceId || ""}
                        onChange={(e) => setNewQuickReplyServiceId(e.target.value || null)}
                      >
                        <option value="">Tous les services (global)</option>
                        {services.map((service) => (
                          <option key={service.id} value={service.id}>
                            {service.name}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-muted-foreground">
                        Si un service est sélectionné, cette réponse n'apparaîtra que pour les conversations de ce service.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>Contenu</Label>
                      <textarea
                        className="w-full min-h-[120px] px-3 py-2 text-sm rounded-md border border-input bg-background resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder="Bonjour ! Comment puis-je vous aider aujourd'hui ?"
                        value={newQuickReplyContent}
                        onChange={(e) => setNewQuickReplyContent(e.target.value)}
                      />
                    </div>
                    <Button
                      onClick={handleSaveQuickReply}
                      disabled={savingQuickReply}
                      className="w-full"
                      variant="gold"
                    >
                      {savingQuickReply ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : editingQuickReply ? (
                        "Mettre à jour"
                      ) : (
                        <>
                          <Plus size={16} />
                          Créer la réponse
                        </>
                      )}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Quick Replies List */}
            <div className="bg-card rounded-xl border border-border overflow-hidden">
              {quickReplies.length === 0 ? (
                <div className="p-8 text-center">
                  <MessageSquare size={40} className="mx-auto text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">
                    Aucune réponse rapide créée
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {quickReplies.map((reply) => (
                    <div
                      key={reply.id}
                      className="flex items-start justify-between px-4 py-3 hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <MessageSquare size={18} className="text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-foreground">{reply.title}</p>
                            {reply.category && (
                              <Badge variant="outline" className="text-xs">
                                {reply.category}
                              </Badge>
                            )}
                            {reply.service_id ? (
                              <Badge variant="secondary" className="text-xs bg-primary/10 text-primary border-primary/20">
                                {services.find(s => s.id === reply.service_id)?.name || "Service"}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs text-muted-foreground">
                                Global
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                            {reply.content}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0 ml-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical size={18} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openQuickReplyDialog(reply)}>
                              <Edit size={14} className="mr-2" />
                              Modifier
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDeleteQuickReply(reply.id)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 size={14} className="mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Banners Tab */}
          <TabsContent value="banners" className="space-y-4">
            {/* Actions */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-medium text-foreground">Annonces promotionnelles</h2>
                <p className="text-sm text-muted-foreground">
                  Bannières affichées sur la page Services
                </p>
              </div>

              <Dialog open={bannerDialogOpen} onOpenChange={setBannerDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="gold" onClick={() => openBannerDialog()}>
                    <Plus size={18} />
                    <span>Nouvelle annonce</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-serif">
                      {editingBanner ? "Modifier l'annonce" : "Créer une annonce"}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Titre</Label>
                      <Input
                        placeholder="Ex: Offre spéciale été"
                        value={newBannerTitle}
                        onChange={(e) => setNewBannerTitle(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Contenu</Label>
                      <textarea
                        className="w-full min-h-[100px] px-3 py-2 text-sm rounded-md border border-input bg-background resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder="Profitez de -20% sur tous nos services premium..."
                        value={newBannerContent}
                        onChange={(e) => setNewBannerContent(e.target.value)}
                      />
                    </div>
                    {!editingBanner && (
                      <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Bell size={16} className="text-primary" />
                          <div>
                            <p className="text-sm font-medium">Envoyer une notification</p>
                            <p className="text-xs text-muted-foreground">
                              Notifier tous les utilisateurs
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={newBannerSendNotification}
                          onCheckedChange={setNewBannerSendNotification}
                        />
                      </div>
                    )}
                    <Button
                      onClick={handleSaveBanner}
                      disabled={savingBanner}
                      className="w-full"
                      variant="gold"
                    >
                      {savingBanner ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : editingBanner ? (
                        "Mettre à jour"
                      ) : (
                        <>
                          <Plus size={16} />
                          Créer l'annonce
                        </>
                      )}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Banners List */}
            <div className="bg-card rounded-xl border border-border overflow-hidden">
              {banners.length === 0 ? (
                <div className="p-8 text-center">
                  <Megaphone size={40} className="mx-auto text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">
                    Aucune annonce créée
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {banners.map((banner) => (
                    <div
                      key={banner.id}
                      className={`flex items-start justify-between px-4 py-3 hover:bg-accent/50 transition-colors ${!banner.is_active ? 'opacity-50' : ''}`}
                    >
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <Megaphone size={18} className="text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-foreground">{banner.title}</p>
                            <Badge variant={banner.is_active ? "default" : "outline"} className="text-xs">
                              {banner.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                            {banner.content}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Créée le {format(new Date(banner.created_at), "dd/MM/yyyy")}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0 ml-2">
                        <Switch
                          checked={banner.is_active}
                          onCheckedChange={() => handleToggleBannerActive(banner)}
                        />
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical size={18} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openBannerDialog(banner)}>
                              <Edit size={14} className="mr-2" />
                              Modifier
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDeleteBanner(banner.id)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 size={14} className="mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}